import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {deleteRecord, fetchRecords} from '../services/BankAccountsService';
import {useState, useEffect} from 'react';
import {BankAccount} from '../types';
import {usePagination} from '../../../hooks/usePagination';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const useBankAccounts = (customerId: string) => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const pagination = usePagination();

    const bankAccountsQuery = useQuery({
        queryKey: ['bankAccounts', customerId, pagination.currentToken],
        queryFn: () => fetchRecords(customerId, pagination.pageSize, pagination.currentToken),
    });

    useEffect(() => {
        if (bankAccountsQuery.data) pagination.setNextToken(bankAccountsQuery.data.nextToken);
    }, [bankAccountsQuery.data]);

    useEffect(() => {
        pagination.resetPagination();
    }, [customerId]);

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(customerId, id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Bank account deleted');
            queryClient.invalidateQueries({queryKey: ['bankAccounts', customerId]});
        },
        onError: notifyError,
    });

    const fetchRecordsAction = () => {
        queryClient.invalidateQueries({queryKey: ['bankAccounts', customerId]});
    };

    const deleteRecords = (ids?: string[]) => {
        if (ids && ids.length > 0) {
            deleteMutation.mutate(ids);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems: BankAccount[] = bankAccountsQuery.data?.items || [];

    const visibleItems = keyword
        ? allItems.filter(item => {
            const search = keyword.toLowerCase();
            return (
                item.id?.toLowerCase().includes(search) ||
                item.accountHolderName?.toLowerCase().includes(search) ||
                item.bankName?.toLowerCase().includes(search) ||
                item.routingNumber?.toLowerCase().includes(search)
            );
        })
        : allItems;

    return {
        items: allItems,
        visibleItems,
        loading: bankAccountsQuery.isLoading,
        keyword,
        visibleCount: visibleItems.length,
        totalCount: allItems.length,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation,
        currentPage: pagination.currentPage,
        hasNext: pagination.hasNext,
        hasPrevious: pagination.hasPrevious,
        goToNextPage: pagination.goToNextPage,
        goToPreviousPage: pagination.goToPreviousPage,
    };
};
